import { useEffect, useRef, useState } from 'react'
import { Link, NavLink } from 'react-router-dom'
import { Menu, X, ChevronDown } from 'lucide-react'
import { tenantConfig } from '../config/tenant'
import Logo from './Logo'
import LeadCaptureModal from './LeadCaptureModal'

const linkBase =
  'text-sm text-gs-text hover:text-ink transition-colors font-medium'
const linkActive = 'text-ink'

const SIMPLE_LINKS: { to: string; label: string; end?: boolean }[] = [
  { to: '/', label: 'Home', end: true },
  { to: '/about', label: 'About Us' },
  { to: '/services', label: 'Services' },
  { to: '/tools', label: 'Tools' },
]

// Learning Hub groups the educational content under one nav item.
const HUB = {
  label: 'Learning Hub',
  children: [
    { to: '/playbook', label: 'Playbooks' },
    { to: '/debt-recycling', label: 'Debt recycling' },
    { to: '/services#faq', label: 'FAQs' },
  ],
}

export default function Nav() {
  const [bookOpen, setBookOpen] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const [hubOpen, setHubOpen] = useState(false)
  const hubRef = useRef<HTMLDivElement>(null)

  // Close the desktop Learning Hub dropdown on outside click or Escape.
  useEffect(() => {
    if (!hubOpen) return
    const onDoc = (e: MouseEvent) => {
      if (hubRef.current && !hubRef.current.contains(e.target as Node)) setHubOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setHubOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDoc)
      document.removeEventListener('keydown', onKey)
    }
  }, [hubOpen])

  return (
    <>
      <nav
        aria-label="Primary"
        className="fixed top-0 left-0 right-0 z-50 bg-[rgba(250,250,247,0.85)] backdrop-blur border-b border-[#E8E8E2]"
      >
        <div className="max-w-7xl mx-auto px-6 py-2 flex items-center justify-between">
          <Link to="/" aria-label={`${tenantConfig.name} home`} onClick={() => setMenuOpen(false)}>
            <Logo size={28} />
          </Link>

          {/* Desktop nav */}
          <div className="hidden md:flex items-center gap-6">
            {SIMPLE_LINKS.map((l) => (
              <NavLink key={l.to} to={l.to} end={l.end} className={({ isActive }) => `${linkBase} ${isActive ? linkActive : ''}`}>
                {l.label}
              </NavLink>
            ))}

            {/* Learning Hub dropdown */}
            <div className="relative" ref={hubRef}>
              <button
                type="button"
                onClick={() => setHubOpen((v) => !v)}
                className={`${linkBase} inline-flex items-center gap-1`}
                aria-haspopup="true"
                aria-expanded={hubOpen}
              >
                {HUB.label}
                <ChevronDown size={14} className={`transition-transform ${hubOpen ? 'rotate-180' : ''}`} aria-hidden="true" />
              </button>
              {hubOpen && (
                <div className="absolute right-0 top-full mt-2 w-48 rounded-xl border border-[#E8E8E2] bg-white shadow-lg py-1.5 z-50">
                  {HUB.children.map((c) => (
                    <NavLink
                      key={c.to}
                      to={c.to}
                      onClick={() => setHubOpen(false)}
                      className={({ isActive }) => `block px-4 py-2.5 text-sm hover:bg-[#F4F4F0] transition-colors ${isActive ? 'text-ink font-medium' : 'text-gs-text'}`}
                    >
                      {c.label}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>

            <button
              onClick={() => setBookOpen(true)}
              className="text-sm font-semibold px-5 py-2 rounded-full transition-opacity hover:opacity-90"
              style={{ background: tenantConfig.primaryColor, color: '#fff' }}
            >
              Book a Call
            </button>
          </div>

          {/* Mobile toggle */}
          <button
            type="button"
            className="md:hidden inline-flex items-center justify-center w-11 h-11 -mr-2 rounded-lg text-ink hover:bg-black/5"
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={menuOpen}
            aria-controls="mobile-menu"
            onClick={() => setMenuOpen((v) => !v)}
          >
            {menuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile menu panel */}
        {menuOpen && (
          <div id="mobile-menu" className="md:hidden border-t border-[#E8E8E2] bg-[rgba(250,250,247,0.98)] backdrop-blur">
            <div className="max-w-7xl mx-auto px-6 py-2 flex flex-col">
              {SIMPLE_LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.end}
                  onClick={() => setMenuOpen(false)}
                  className={({ isActive }) => `py-3 text-base border-b border-[#EEEEE8] ${linkBase} ${isActive ? linkActive : ''}`}
                >
                  {l.label}
                </NavLink>
              ))}

              {/* Learning Hub group */}
              <div className="py-3 border-b border-[#EEEEE8]">
                <div className="text-xs font-semibold uppercase tracking-wider text-gs-text-muted mb-1">{HUB.label}</div>
                <div className="flex flex-col pl-1">
                  {HUB.children.map((c) => (
                    <NavLink
                      key={c.to}
                      to={c.to}
                      onClick={() => setMenuOpen(false)}
                      className={({ isActive }) => `py-2 text-base ${linkBase} ${isActive ? linkActive : ''}`}
                    >
                      {c.label}
                    </NavLink>
                  ))}
                </div>
              </div>

              <button
                onClick={() => {
                  setMenuOpen(false)
                  setBookOpen(true)
                }}
                className="mt-4 mb-2 text-sm font-semibold px-5 py-3 rounded-full transition-opacity hover:opacity-90"
                style={{ background: tenantConfig.primaryColor, color: '#fff' }}
              >
                Book a Call
              </button>
            </div>
          </div>
        )}
      </nav>

      <LeadCaptureModal open={bookOpen} onClose={() => setBookOpen(false)} source="website" />
    </>
  )
}
